import { Icon, IconSizeClasses, Input } from '@eppendorf/vnls-react-components';
import { useCallback, useState, useEffect } from 'react';
import { useDebounce } from 'react-use';

/**
 * This component provides a text input field for search queries with debouncing.
 * It can operate in two modes:
 *
 * 1. Controlled Mode:
 *    - If a `value` prop is provided, the component's displayed text is controlled
 *      by this prop.
 *    - Changes to the `value` prop from the parent component will update the
 *      input field.
 *    - User input updates an internal state (`inputValue`) and, after a debounce
 *      delay, calls `onSearchQueryChange` with the trimmed input. The parent
 *      component is then responsible for updating the `value` prop, typically
 *      by updating URL search parameters or other state.
 *
 * 2. Uncontrolled Mode:
 *    - If the `value` prop is NOT provided, the component manages its own internal
 *      state (`inputValue`) for the text input.
 *    - User input directly updates this internal state.
 *    - After a debounce delay, `onSearchQueryChange` is called with the trimmed
 *      internal state.
 *
 * In both modes, a clear button is provided to reset the input and immediately
 * call `onSearchQueryChange` with an empty string.
 */
export interface SearchInputProps {
  onSearchQueryChange: (value: string) => void;
  value?: string;
  placeholder?: string;
  id?: string;
  debounceDelay?: number;
  className?: string;
}

export function SearchInput(props: SearchInputProps) {
  const {
    value: propsValue,
    onSearchQueryChange,
    placeholder,
    id,
    debounceDelay = 500,
    className,
  } = props;

  // inputValue is the internal state for the text input.
  // It's initialized with propsValue if provided (controlled mode start),
  // otherwise defaults to an empty string (uncontrolled mode start).
  const [inputValue, setInputValue] = useState<string>(propsValue ?? '');

  // Effect to synchronize inputValue with propsValue when propsValue changes.
  // This is primarily for controlled mode to reflect external updates to the value.
  useEffect(() => {
    if (propsValue !== undefined && propsValue !== inputValue) {
      setInputValue(propsValue);
    }
  }, [propsValue]);

  const [, cancelDebounce] = useDebounce(
    () => {
      onSearchQueryChange(inputValue.trim());
    },
    debounceDelay,
    [inputValue, onSearchQueryChange], // Debounce based on internal inputValue
  );

  const handleInputChange = useCallback((currentInputText: string): void => {
    setInputValue(currentInputText); // Update internal state on every keystroke
  }, []);

  const handleClearSearch = useCallback((): void => {
    setInputValue('');
    cancelDebounce();
    onSearchQueryChange(''); // Immediately call with empty string when clearing
  }, [onSearchQueryChange, cancelDebounce]);

  return (
    <Input
      type="text"
      value={inputValue}
      onChange={(e) => handleInputChange(e.target.value)}
      placeholder={placeholder}
      id={id || 'search-input'}
      leadingVisual={<Icon name="search" size={IconSizeClasses.XSmall} />}
      className={className}
      trailingAction={
        <Input.Action
          onClick={handleClearSearch}
          icon={
            inputValue ? (
              <Icon name="close" size={IconSizeClasses.XSmall} />
            ) : (
              <div style={{ width: '1.5rem', height: '1.5rem' }} />
            )
          }
        />
      }
    />
  );
}
